import { useCallback, useEffect, useRef, useState } from "react";
import { DigiCalls, type Call, type Offer } from "@digitalsac/digicalls-sdk";
import { prepareIncomingOffer } from "./offerLifecycle";

export type Theme = "light" | "dark" | "system";

export interface WebphoneConfig {
  baseUrl: string;
  tokens?: string[];
  deviceNames?: Record<string, string>;
  lang?: "pt" | "en" | "es";
  /** initial theme; can also be changed at runtime via the render() handle. */
  theme?: Theme;
  /** start hidden (background); show via the handle .open(true) or an incoming call. */
  startMinimized?: boolean;
  to?: string;
  /**
   * Optional gate for incoming offers (e.g. Digitalsac wallet routing).
   * Return false to ignore the offer locally without rejecting it for other clients.
   */
  filterOffer?: (offer: Offer) => boolean | Promise<boolean>;
}

/** Imperative controls the host app can call (e.g., to sync theme). */
export interface Controller {
  setTheme?: (t: Theme) => void;
  setLang?: (l: "pt" | "en" | "es") => void;
  dial?: (to: string) => void;
  open?: (open: boolean) => void;
}

const resolveDark = (t?: Theme): boolean =>
  t === "dark" ? true : t === "system" ? (window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false) : false;

const STR = {
  pt: { title: "Chamadas", settings: "Configurações", settingsSub: "Configure todo o webphone", numbers: "Números", prefs: "Preferências", diag: "Diagnóstico", theme: "Tema", light: "Claro", dark: "Escuro", system: "Sistema", lang: "Idioma", number: "Número", type: "Digite…", call: "Ligar", incoming: "Chamada recebida", accept: "Atender", reject: "Recusar", end: "Encerrar", mute: "Mudo", unmute: "Ativar", connected: "Conectado", disconnected: "Desconectado", ringing: "Chamando…", oncall: "Em chamada", token: "Token", browser: "Navegador", network: "Rede", audio: "Áudio", micperm: "permissão do microfone", inputs: "entradas", outputs: "saídas", teststun: "Testar STUN", copyreport: "Copiar relatório", noline: "Adicione um número (token) na engrenagem ⚙", peerMuted: "Cliente silenciou o microfone" },
  en: { title: "Calls", settings: "Settings", settingsSub: "Configure the whole webphone", numbers: "Numbers", prefs: "Preferences", diag: "Diagnostics", theme: "Theme", light: "Light", dark: "Dark", system: "System", lang: "Language", number: "Number", type: "Type…", call: "Call", incoming: "Incoming call", accept: "Accept", reject: "Reject", end: "End", mute: "Mute", unmute: "Unmute", connected: "Connected", disconnected: "Disconnected", ringing: "Ringing…", oncall: "On call", token: "Token", browser: "Browser", network: "Network", audio: "Audio", micperm: "microphone permission", inputs: "inputs", outputs: "outputs", teststun: "Test STUN", copyreport: "Copy report", noline: "Add a number (token) in the gear ⚙", peerMuted: "Customer muted their mic" },
  es: { title: "Llamadas", settings: "Configuración", settingsSub: "Configura todo el webphone", numbers: "Números", prefs: "Preferencias", diag: "Diagnóstico", theme: "Tema", light: "Claro", dark: "Oscuro", system: "Sistema", lang: "Idioma", number: "Número", type: "Escribe…", call: "Llamar", incoming: "Llamada entrante", accept: "Atender", reject: "Rechazar", end: "Finalizar", mute: "Silenciar", unmute: "Activar", connected: "Conectado", disconnected: "Desconectado", ringing: "Llamando…", oncall: "En llamada", token: "Token", browser: "Navegador", network: "Red", audio: "Audio", micperm: "permiso del micrófono", inputs: "entradas", outputs: "salidas", teststun: "Probar STUN", copyreport: "Copiar informe", noline: "Agrega un número (token) en el engranaje ⚙", peerMuted: "El cliente silenció su micrófono" },
};

const KEYS: { d: string; l: string }[] = [
  { d: "1", l: "" }, { d: "2", l: "ABC" }, { d: "3", l: "DEF" },
  { d: "4", l: "GHI" }, { d: "5", l: "JKL" }, { d: "6", l: "MNO" },
  { d: "7", l: "PQRS" }, { d: "8", l: "TUV" }, { d: "9", l: "WXYZ" },
  { d: "*", l: "" }, { d: "0", l: "+" }, { d: "#", l: "" },
];

const fmt = (s: number) => `${String(Math.floor(s / 60)).padStart(2, "0")}:${String(s % 60).padStart(2, "0")}`;

let actx: AudioContext | null = null;
const DTMF: Record<string, [number, number]> = {
  "1": [697, 1209], "2": [697, 1336], "3": [697, 1477], "4": [770, 1209], "5": [770, 1336],
  "6": [770, 1477], "7": [852, 1209], "8": [852, 1336], "9": [852, 1477], "*": [941, 1209], "0": [941, 1336], "#": [941, 1477],
};
function tone(d: string) {
  const f = DTMF[d];
  if (!f) return;
  try {
    actx = actx || new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
    if (actx.state === "suspended") void actx.resume();
    const now = actx.currentTime;
    const g = actx.createGain();
    // short envelope (avoids clicks) at a phone-like volume
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.2, now + 0.008);
    g.gain.setValueAtTime(0.2, now + 0.11);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.15);
    g.connect(actx.destination);
    f.forEach((freq) => {
      const o = actx!.createOscillator();
      o.type = "sine";
      o.frequency.value = freq;
      o.connect(g);
      o.start(now);
      o.stop(now + 0.17);
    });
  } catch {
    /* noop */
  }
}

type Screen = "keypad" | "incoming" | "active";
type Tab = "numbers" | "prefs" | "diag";

export function App({ config, digicalls, controller }: { config: WebphoneConfig; digicalls: DigiCalls; controller: Controller }) {
  const [lang, setLang] = useState<"pt" | "en" | "es">(config.lang ?? "pt");
  const t = STR[lang];
  const [open, setOpen] = useState(!config.startMinimized);
  const [dark, setDark] = useState(() => resolveDark(config.theme));
  const [screen, setScreen] = useState<Screen>("keypad");
  const [number, setNumber] = useState(config.to ?? "");
  const [busy, setBusy] = useState(false);
  const [offer, setOffer] = useState<Offer | null>(null);
  const [call, setCall] = useState<Call | null>(null);
  const [connected, setConnected] = useState(false);
  const [muted, setMuted] = useState(false);
  const [peerMuted, setPeerMuted] = useState(false);
  const [secs, setSecs] = useState(0);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);

  // settings
  const [settings, setSettings] = useState(false);
  const [tab, setTab] = useState<Tab>("numbers");
  const [tokens, setTokens] = useState<string[]>(config.tokens ?? []);
  const [devices, setDevices] = useState(() => digicalls.getDevices());
  const [newTok, setNewTok] = useState("");
  const [reveal, setReveal] = useState<Record<string, boolean>>({});
  const [stun, setStun] = useState("");
  const [lineSel, setLineSel] = useState("");
  const [peerMap, setPeerMap] = useState<Record<string, { phone?: string; name?: string; avatar?: string }>>({});

  useEffect(() => {
    digicalls.onUpdate(() => setDevices(digicalls.getDevices()));
    digicalls.onPeerInfo((info) =>
      setPeerMap((m) => ({ ...m, [info.callId]: { phone: info.phone, name: info.name, avatar: info.avatar } })),
    );
  }, [digicalls]);

  const filterOfferRef = useRef(config.filterOffer);
  filterOfferRef.current = config.filterOffer;

  useEffect(() => {
    if (!connected) return;
    setSecs(0);
    const id = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(id);
  }, [connected]);

  // Ringback (outgoing "calling") + ringtone (incoming) via WebAudio, no assets.
  const ringRef = useRef<{ stop: () => void } | null>(null);
  const stopRing = () => {
    ringRef.current?.stop();
    ringRef.current = null;
  };
  const startRing = (kind: "in" | "out") => {
    stopRing();
    let ctx: AudioContext;
    try {
      actx =
        actx ||
        new (window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext)();
      ctx = actx;
      if (ctx.state === "suspended") void ctx.resume();
    } catch {
      return;
    }
    let cancelled = false;
    const burst = (freqs: number[], dur: number, when: number) => {
      const g = ctx.createGain();
      g.gain.value = 0.0001;
      g.connect(ctx.destination);
      const t0 = ctx.currentTime + when;
      g.gain.setValueAtTime(0.0001, t0);
      g.gain.exponentialRampToValueAtTime(0.14, t0 + 0.03);
      g.gain.setValueAtTime(0.14, t0 + dur - 0.05);
      g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
      freqs.forEach((f) => {
        const o = ctx.createOscillator();
        o.frequency.value = f;
        o.connect(g);
        o.start(t0);
        o.stop(t0 + dur + 0.02);
      });
    };
    const cycle = () => {
      if (cancelled) return;
      if (kind === "out") burst([425], 1.0, 0); // ringback BR: 1s on, 4s off
      else {
        burst([440, 480], 0.4, 0); // ring: dois toques curtos
        burst([440, 480], 0.4, 0.6);
      }
    };
    cycle();
    const id = setInterval(cycle, kind === "out" ? 5000 : 3000);
    ringRef.current = {
      stop: () => {
        cancelled = true;
        clearInterval(id);
      },
    };
  };

  useEffect(() => {
    digicalls.onOffer((o) => {
      void (async () => {
        const dismiss = () => {
          stopRing();
          setOffer((prev) => (prev?.callId === o.callId ? null : prev));
          setScreen((s) => (s === "incoming" ? "keypad" : s));
        };
        const shouldShow = await prepareIncomingOffer(
          o,
          filterOfferRef.current,
          dismiss,
        );
        if (!shouldShow) return;

        setOffer(o);
        setScreen("incoming");
        setOpen(true);
      })();
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [digicalls]);

  useEffect(() => {
    const incoming = screen === "incoming";
    const outgoing = screen === "active" && !!call && !connected;
    if (incoming) startRing("in");
    else if (outgoing) startRing("out");
    else stopRing();
    return stopRing;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [screen, connected, call]);

  const selected = lineSel || devices.find((d) => d.connected)?.token || tokens[0] || "";

  const attach = useCallback((c: Call) => {
    setCall(c);
    setMuted(false);
    setPeerMuted(c.peerMuted);
    c.onPeerAccept(() => setConnected(true));
    c.onPeerMuteChange((m) => setPeerMuted(m));
    c.onEnd(() => {
      setScreen("keypad");
      setCall(null);
      setConnected(false);
      setPeerMuted(false);
    });
  }, []);

  const startCallTo = async (raw: string) => {
    const to = raw.replace(/[^0-9]/g, "");
    if (!to || !selected) return;
    setBusy(true);
    try {
      const { call: c, err } = await digicalls.startCall({ fromTokens: [selected], to });
      if (err || !c) {
        alert(err ?? "erro");
        return;
      }
      attach(c);
      setConnected(false);
      setScreen("active");
    } finally {
      setBusy(false);
    }
  };
  const dial = () => startCallTo(number);

  // Expose imperative controls so the host app can drive the widget (theme/lang/dial).
  controller.setTheme = (th) => setDark(resolveDark(th));
  controller.setLang = (l) => setLang(l);
  controller.open = (o) => setOpen(o);
  controller.dial = (to) => {
    setOpen(true);
    setScreen("keypad");
    setNumber(to);
    void startCallTo(to);
  };

  const accept = async () => {
    if (!offer) return;
    setBusy(true);
    try {
      attach(await offer.accept());
      setConnected(true);
      setScreen("active");
      setOffer(null);
    } finally {
      setBusy(false);
    }
  };
  const reject = async () => {
    await offer?.reject().catch(() => {});
    setOffer(null);
    setScreen("keypad");
  };
  const hangup = async () => {
    await call?.end().catch(() => {});
    setScreen("keypad");
    setCall(null);
    setConnected(false);
  };
  const toggleMute = () => {
    if (!call) return;
    muted ? call.unmute() : call.mute();
    setMuted(!muted);
  };

  const press = (d: string) => {
    tone(d);
    setNumber((n) => n + d);
  };

  // devices tab
  const addToken = async () => {
    const tok = newTok.trim();
    if (!tok || tokens.includes(tok)) return;
    setTokens((ts) => [...ts, tok]);
    setNewTok("");
    await digicalls.addDevices([tok]);
  };
  const toggleToken = async (tok: string, on: boolean) => {
    if (on) digicalls.removeDevices([tok]);
    else await digicalls.addDevices([tok]);
    setDevices(digicalls.getDevices());
  };
  const delToken = (tok: string) => {
    digicalls.removeDevices([tok]);
    setTokens((ts) => ts.filter((x) => x !== tok));
  };
  const isOn = (tok: string) => devices.some((d) => d.token === tok);
  const isConn = (tok: string) => devices.find((d) => d.token === tok)?.connected ?? false;
  const numberOf = (tok: string) => devices.find((d) => d.token === tok)?.number ?? "";

  const testStun = async () => {
    setStun("…");
    try {
      const pc = new RTCPeerConnection({ iceServers: [{ urls: "stun:stun.l.google.com:19302" }] });
      pc.createDataChannel("x");
      let ok = false;
      pc.onicecandidate = (e) => {
        if (e.candidate && e.candidate.type === "srflx") ok = true;
      };
      await pc.setLocalDescription(await pc.createOffer());
      await new Promise((r) => setTimeout(r, 2500));
      pc.close();
      setStun(ok ? "OK ✓" : "sem srflx");
    } catch {
      setStun("erro");
    }
  };

  const copyReport = () => {
    const nav = navigator as Navigator & { connection?: { effectiveType?: string; downlink?: number; rtt?: number } };
    const rep = { ua: navigator.userAgent, online: navigator.onLine, conn: nav.connection ?? null, devices };
    navigator.clipboard?.writeText(JSON.stringify(rep, null, 2)).catch(() => {});
  };

  // drag
  const dragRef = useRef<{ dx: number; dy: number } | null>(null);
  const onDown = (e: React.PointerEvent) => {
    const r = (e.currentTarget.parentElement as HTMLElement).getBoundingClientRect();
    dragRef.current = { dx: e.clientX - r.left, dy: e.clientY - r.top };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onMove = (e: React.PointerEvent) => {
    if (dragRef.current) setPos({ left: e.clientX - dragRef.current.dx, top: e.clientY - dragRef.current.dy });
  };
  const onUp = () => (dragRef.current = null);

  const style = pos ? { left: pos.left, top: pos.top, right: "auto" as const, bottom: "auto" as const } : { right: 20, bottom: 20 };
  const rawPeer = (screen === "incoming" ? offer?.peer.phone : call?.peer.phone) ?? "";
  const curId = screen === "incoming" ? offer?.callId : call?.callId;
  const pi = curId ? peerMap[curId] : undefined;
  const peerPhone = pi?.phone || rawPeer;
  const peerName = pi?.name || "";
  const peerAvatar = pi?.avatar;
  const peerTitle = peerName || peerPhone;

  // Closed = fully hidden (stays mounted/active in the background; an incoming
  // call re-opens it). No floating launcher icon.
  if (!open) return null;

  return (
    <div className={"dc" + (dark ? " dark" : "")}>
      <div className="dc-wrap" style={style}>
        <div className="dc-head" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp}>
          <button className="dc-ico" aria-label="notificações">🔔</button>
          <button className="dc-ico" onClick={() => setSettings(true)} aria-label={t.settings}>⚙</button>
          <button className="dc-ico" onClick={() => setOpen(false)} aria-label="fechar">✕</button>
        </div>

        {screen === "keypad" && (
          <>
            <div className="dc-body">
              {devices.length >= 1 && (
                <select className="dc-line" value={selected} onChange={(e) => setLineSel(e.target.value)} disabled={devices.length < 2}>
                  {devices.map((d) => (
                    <option key={d.token} value={d.token}>
                      {(d.number || d.name)}{d.number && d.name && d.name !== "WhatsApp" ? ` · ${d.name}` : ""} {d.connected ? "●" : "○"}
                    </option>
                  ))}
                </select>
              )}
              <div className={"dc-disp" + (number ? "" : " ph")}>{number || t.type}</div>
              <div className="dc-keys">
                {KEYS.map((k) => (
                  <button key={k.d} className="dc-key" onClick={() => press(k.d)}>
                    <b>{k.d}</b>
                    {k.l && <span>{k.l}</span>}
                  </button>
                ))}
              </div>
              <div className="dc-actions">
                <button className="dc-call" onClick={dial} disabled={busy || !number || !selected} aria-label={t.call}>☎</button>
                <button className="dc-bksp" onClick={() => setNumber((n) => n.slice(0, -1))} aria-label="del">⌫</button>
              </div>
              {!selected && <div className="dc-label" style={{ textAlign: "center" }}>{t.noline}</div>}
            </div>
            <div className="dc-foot">DigiCalls · v0.3.4</div>
          </>
        )}

        {screen === "incoming" && (
          <div className="dc-body dc-center">
            <div className="dc-av">{peerAvatar ? <img className="dc-av-img" src={peerAvatar} alt="" /> : "☎"}</div>
            <div className="dc-status">{t.incoming}</div>
            <div className="dc-peer">{peerTitle}</div>
            {peerName && <div className="dc-status">{peerPhone}</div>}
            <div className="dc-row">
              <button className="dc-btn dc-reject" onClick={reject}>{t.reject}</button>
              <button className="dc-btn dc-accept" onClick={accept} disabled={busy}>{t.accept}</button>
            </div>
          </div>
        )}

        {screen === "active" && (
          <div className="dc-body dc-center">
            <div className="dc-av">{peerAvatar ? <img className="dc-av-img" src={peerAvatar} alt="" /> : "☎"}</div>
            <div className="dc-peer">{peerTitle}</div>
            {peerName && <div className="dc-status">{peerPhone}</div>}
            <div className="dc-status">{connected ? t.oncall : t.ringing}</div>
            {peerMuted && (
              <div className="dc-peer-mute" role="status">
                <svg className="dc-peer-mute-ico" viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
                  <path fill="currentColor" d="M19 11c0 1.19-.34 2.3-.9 3.28l-1.23-1.23c.27-.62.43-1.31.43-2.05h2zm-4-.16.06.06L15 11.1V5c0-1.66-1.34-3-3-3S9 3.34 9 5v.17l6 6.01zM4.27 3 3 4.27l6.01 6.01H9.02c0 .63.13 1.24.36 1.79L4.27 3zm12.66 12.66L19.73 18 21 16.73 4.27 0 3 1.27l5.58 5.58A5.99 5.99 0 0 0 5 11h1.7c0-1.16.44-2.2 1.15-3l1.42 1.42C8.5 10.05 8.3 10.5 8.3 11H5c0 2.76 1.89 5.1 4.5 5.8V19h-2v2h7v-2h-2v-2.18c.9-.24 1.71-.68 2.4-1.27l2.03 2.03z" />
                </svg>
                {t.peerMuted}
              </div>
            )}
            <div className="dc-timer">{fmt(secs)}</div>
            <div className="dc-row">
              <button className={"dc-btn dc-sec" + (muted ? " on" : "")} onClick={toggleMute}>{muted ? t.unmute : t.mute}</button>
              <button className="dc-btn dc-endbtn" onClick={hangup}>{t.end}</button>
            </div>
          </div>
        )}
      </div>

      {settings && (
        <div className="dc-ovl" onClick={(e) => e.target === e.currentTarget && setSettings(false)}>
          <div className="dc-modal">
            <div className="dc-mhead">
              <div>
                <div className="dc-mtitle">{t.settings}</div>
                <div className="dc-msub">{t.settingsSub}</div>
              </div>
              <button className="dc-ico" style={{ background: "#0002", color: "var(--fg)" }} onClick={() => setSettings(false)}>✕</button>
            </div>
            <div className="dc-tabs">
              {([["numbers", t.numbers], ["prefs", t.prefs], ["diag", t.diag]] as [Tab, string][]).map(([k, label]) => (
                <button key={k} className={"dc-tab" + (tab === k ? " on" : "")} onClick={() => setTab(k)}>{label}</button>
              ))}
            </div>

            <div className="dc-mbody">
              {tab === "numbers" && (
                <>
                  <div className="dc-add">
                    <input className="dc-input" placeholder={t.token} value={newTok} onChange={(e) => setNewTok(e.target.value)} />
                    <button className="dc-plus" onClick={addToken}>＋</button>
                  </div>
                  {tokens.map((tok) => (
                    <div className="dc-dev" key={tok}>
                      <div className="dc-dev-top">
                        <div className="dc-st">
                          <span className={"dc-dot " + (isConn(tok) ? "on" : "off")} />
                          {isConn(tok) ? t.connected : t.disconnected}
                          {numberOf(tok) ? ` · ${numberOf(tok)}` : ""}
                        </div>
                        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                          <button className={"dc-tg" + (isOn(tok) ? " on" : "")} onClick={() => toggleToken(tok, isOn(tok))} aria-label="toggle"><i /></button>
                          <button className="dc-trash" onClick={() => delToken(tok)} aria-label="del">🗑</button>
                        </div>
                      </div>
                      <div className="dc-dev-tok">
                        <span>{reveal[tok] ? tok : "•".repeat(12)}</span>
                        <button className="dc-mini" onClick={() => setReveal((r) => ({ ...r, [tok]: !r[tok] }))}>👁</button>
                        <button className="dc-mini" onClick={() => navigator.clipboard?.writeText(tok).catch(() => {})}>⧉</button>
                      </div>
                    </div>
                  ))}
                </>
              )}

              {tab === "prefs" && (
                <>
                  <div className="dc-sec-box">
                    <div className="dc-sec-h">{t.theme}</div>
                    <div className="dc-opts">
                      <button className={"dc-opt" + (!dark ? " on" : "")} onClick={() => setDark(false)}>☀ {t.light}</button>
                      <button className={"dc-opt" + (dark ? " on" : "")} onClick={() => setDark(true)}>🌙 {t.dark}</button>
                      <button className="dc-opt" onClick={() => setDark(window.matchMedia?.("(prefers-color-scheme: dark)").matches ?? false)}>💻 {t.system}</button>
                    </div>
                  </div>
                  <div className="dc-sec-box">
                    <div className="dc-sec-h">{t.lang}</div>
                    <div className="dc-opts">
                      <button className={"dc-opt" + (lang === "en" ? " on" : "")} onClick={() => setLang("en")}>EN</button>
                      <button className={"dc-opt" + (lang === "pt" ? " on" : "")} onClick={() => setLang("pt")}>PT-BR</button>
                      <button className={"dc-opt" + (lang === "es" ? " on" : "")} onClick={() => setLang("es")}>ES</button>
                    </div>
                  </div>
                </>
              )}

              {tab === "diag" && (
                <DiagTab t={t} stun={stun} onStun={testStun} onCopy={copyReport} devices={devices} />
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function DiagTab({ t, stun, onStun, onCopy }: { t: (typeof STR)["pt"]; stun: string; onStun: () => void; onCopy: () => void; devices: unknown }) {
  const [audio, setAudio] = useState({ perm: "?", inputs: 0, outputs: 0 });
  useEffect(() => {
    navigator.mediaDevices?.enumerateDevices().then((list) => {
      setAudio({
        perm: list.some((d) => d.kind === "audioinput" && d.label) ? "granted" : "prompt",
        inputs: list.filter((d) => d.kind === "audioinput").length,
        outputs: list.filter((d) => d.kind === "audiooutput").length,
      });
    });
  }, []);
  const nav = navigator as Navigator & { connection?: { effectiveType?: string; downlink?: number; rtt?: number } };
  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        <button className="dc-ghost" onClick={onCopy}>⧉ {t.copyreport}</button>
      </div>
      <div className="dc-sec-box">
        <div className="dc-sec-h">{t.browser}</div>
        <div className="dc-mono">{navigator.userAgent}</div>
      </div>
      <div className="dc-sec-box">
        <div className="dc-sec-h">{t.network}</div>
        <div className="dc-kv"><span className="k">online</span><span>{String(navigator.onLine)}</span></div>
        <div className="dc-kv"><span className="k">effectiveType</span><span>{nav.connection?.effectiveType ?? "—"}</span></div>
        <div className="dc-kv"><span className="k">downlink</span><span>{nav.connection?.downlink ?? "—"}</span></div>
        <div className="dc-kv"><span className="k">rtt</span><span>{nav.connection?.rtt ?? "—"}</span></div>
      </div>
      <div className="dc-sec-box">
        <div className="dc-sec-h">{t.audio}</div>
        <div className="dc-kv"><span className="k">{t.micperm}</span><span>{audio.perm}</span></div>
        <div className="dc-kv"><span className="k">{t.inputs}</span><span>{audio.inputs}</span></div>
        <div className="dc-kv"><span className="k">{t.outputs}</span><span>{audio.outputs}</span></div>
      </div>
      <div className="dc-sec-box">
        <div className="dc-sec-h">STUN</div>
        <button className="dc-pill" onClick={onStun}>{t.teststun}</button> <span className="dc-mono">{stun}</span>
      </div>
    </>
  );
}
